import React, {
    createContext,
    useMemo,
    type CSSProperties,
    type Key,
    type ReactElement,
    type ReactNode
} from 'react';

import classnames from 'classnames';

import { LIST_CONTAINER, LOADING_OVERLAY } from '../../../dataTestIds';
import noResultsImgSrc from '../../assets/images/storybook/no-results-generic-question.svg';
import { type SizeType } from '../../features/config';
import useTranslation from '../../features/locale/useTranslation';
import { Loading } from '../Loading';
import { Text } from '../Typography';

import styles from './List.module.scss';

export type ColumnCount = number;

export interface ListGridType {
    gutter?: number;
    column?: ColumnCount;
    xs?: ColumnCount;
    sm?: ColumnCount;
    md?: ColumnCount;
    lg?: ColumnCount;
    xl?: ColumnCount;
    xxl?: ColumnCount;
}

export type ListProps<T> = {
    className?: string;
    loading?: boolean;
    id?: string;
    style?: CSSProperties;
    children?: ReactNode;
    /**
     * Whether to show border around the list
     */
    bordered?: boolean;
    /**
     * Array of data to be rendered in the list
     */
    dataSource?: T[];
    /**
     * The grid type of list. You can set grid to something
     * like {gutter: 16, column: 4}
     */
    grid?: ListGridType;
    /**
     * Item's unique value, could be an Item's key which holds a
     * unique value of type React.Key or function that receives
     * Item and returns a React.Key
     */
    rowKey?: ((item: T) => Key) | keyof T;
    /**
     * Customize list item when using dataSource
     */
    renderItem?: (item: T, index: number) => ReactNode;
    /**
     * Whether to show split lines between items
     */
    split?: boolean;
    size?: SizeType;
    header?: ReactNode;
    footer?: ReactNode;
    showActionsDivider?: boolean;
    stickX?: boolean;
    stickY?: boolean;
};

export const ListContext = createContext<{
    showActionsDivider?: boolean;
}>({});

export const List = <T,>({
    className,
    loading,
    id,
    style,
    children,
    bordered,
    dataSource,
    grid,
    rowKey,
    renderItem,
    split = true,
    size = 'medium',
    header,
    footer,
    showActionsDivider,
    stickX,
    stickY,
    ...rest
}: ListProps<T>): ReactElement => {
    const { t } = useTranslation();

    // Determine list content
    const listContent = useMemo(() => {
        const isEmptyDataSource =
            !dataSource || (Array.isArray(dataSource) && dataSource.length === 0);

        if (isEmptyDataSource && !!renderItem && !children) {
            return (
                <div className={styles.emptyState}>
                    <div className={styles.image}>
                        <img src={noResultsImgSrc} alt={t('No data')} />
                    </div>
                    <Text color="placeholder">{t('No data')}</Text>
                </div>
            );
        }

        if (typeof renderItem === 'function' && dataSource) {
            const items = dataSource.map((item, index) => {
                let key: Key;
                if (!rowKey) {
                    key = index;
                } else if (typeof rowKey === 'function') {
                    key = rowKey(item);
                } else {
                    key = item[rowKey as keyof T] as Key;
                }
                return <React.Fragment key={key}>{renderItem(item, index)}</React.Fragment>;
            });

            if (grid) {
                const columnCount = grid.column || 1;
                const gutter = grid.gutter || 0;
                const colPadding = gutter / 2;
                const widthPercentage = `${100 / columnCount}%`;

                return (
                    <div
                        className={styles.gridRow}
                        style={{ marginLeft: `-${colPadding}px`, marginRight: `-${colPadding}px` }}
                    >
                        {items.map((item) => (
                            <div
                                key={item?.key}
                                className={styles.gridCol}
                                style={{
                                    paddingLeft: `${colPadding}px`,
                                    paddingRight: `${colPadding}px`,
                                    width: widthPercentage,
                                    maxWidth: widthPercentage
                                }}
                            >
                                {item}
                            </div>
                        ))}
                    </div>
                );
            }

            return items;
        }

        return children;
    }, [dataSource, renderItem, rowKey, children, grid?.column, grid?.gutter, t]);

    const gridClasses = grid
        ? {
              [styles[`grid-column-${grid.column}`]]: !!grid.column,
              [styles[`grid-xs-${grid.xs}`]]: !!grid.xs,
              [styles[`grid-sm-${grid.sm}`]]: !!grid.sm,
              [styles[`grid-md-${grid.md}`]]: !!grid.md,
              [styles[`grid-lg-${grid.lg}`]]: !!grid.lg,
              [styles[`grid-xl-${grid.xl}`]]: !!grid.xl,
              [styles[`grid-xxl-${grid.xxl}`]]: !!grid.xxl
          }
        : {};

    // Apply gutter as inline style (overridden by gridRow for grid layout)
    const listContainerStyle: CSSProperties = grid?.gutter ? { gap: `${grid.gutter}px` } : {};

    const contextValue = useMemo(() => ({ showActionsDivider }), [showActionsDivider]);

    return (
        <ListContext.Provider value={contextValue}>
            <div
                id={id}
                style={style}
                data-testid={LIST_CONTAINER}
                className={classnames(
                    styles.list,
                    {
                        [styles.grid]: grid,
                        [styles.bordered]: bordered,
                        [styles[size]]: size,
                        [styles.split]: split,
                        [styles.showActionsDivider]: showActionsDivider,
                        [styles.stickX]: stickX,
                        [styles.stickY]: stickY,
                        [styles.loading]: loading
                    },
                    className
                )}
                {...rest}
            >
                {header && <div className={styles.header}>{header}</div>}
                <div
                    className={classnames(
                        styles.listContainer,
                        grid ? styles.grid : '',
                        gridClasses
                    )}
                    data-testid="list-content"
                    style={listContainerStyle}
                >
                    {listContent}
                </div>
                {footer && <div className={styles.footer}>{footer}</div>}
                {loading && (
                    <div className={styles.loadingOverlay} data-testid={LOADING_OVERLAY}>
                        <Loading />
                    </div>
                )}
            </div>
        </ListContext.Provider>
    );
};
